"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, Badge, Section, Button, Input } from "@/src/components";
import { useDebounce } from "@/src/hooks/use-debounce";
import { useClipboard } from "@/src/hooks/use-clipboard";
import { useMediaQuery, usePrefersDarkMode } from "@/src/hooks/use-media-query";
import { useLocalStorage } from "@/src/hooks/use-local-storage";

export default function DocsHooks() {
  // useDebounce demo
  const [inputValue, setInputValue] = React.useState("");
  const debouncedValue = useDebounce(inputValue, 500);

  // useClipboard demo
  const { copied, copy } = useClipboard();

  // useMediaQuery demo
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const prefersDark = usePrefersDarkMode();

  // useLocalStorage demo
  const [count, setCount] = useLocalStorage("demo-count", 0);

  return (
    <>
      {/* Page Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Hooks
            </Text>
            <Badge variant="secondary">Utilities</Badge>
          </div>
          <Text as="p" className="text-lg text-muted-foreground max-w-2xl">
            A collection of reusable React hooks for common patterns like debouncing,
            clipboard operations, media queries, and more.
          </Text>
        </div>
      </Section>

      {/* useDebounce */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>useDebounce</CardTitle>
              <CardDescription>Debounce a value with a specified delay</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="space-y-4 max-w-md">
            <Input
              placeholder="Type something..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <div className="text-sm space-y-1">
              <Text as="p" className="text-muted-foreground">
                Input value: <span className="text-foreground font-medium">{inputValue || "(empty)"}</span>
              </Text>
              <Text as="p" className="text-muted-foreground">
                Debounced (500ms): <span className="text-foreground font-medium">{debouncedValue || "(empty)"}</span>
              </Text>
            </div>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{`import { useDebounce } from "@/hooks";`}</code>
            <code className="block mt-2">{`const [value, setValue] = useState("");`}</code>
            <code className="block">{`const debouncedValue = useDebounce(value, 500);`}</code>
            <code className="block mt-2">{`// debouncedValue updates 500ms after value stops changing`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* useClipboard */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>useClipboard</CardTitle>
              <CardDescription>Copy text to clipboard with feedback</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="flex items-center gap-4">
            <Button onClick={() => copy("Hello, World!")}>
              {copied ? "Copied!" : "Copy to Clipboard"}
            </Button>
            <Text as="p" className="text-sm text-muted-foreground">
              Copies &quot;Hello, World!&quot;
            </Text>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{`import { useClipboard } from "@/hooks";`}</code>
            <code className="block mt-2">{`const { copied, copy, error } = useClipboard();`}</code>
            <code className="block mt-2">{`<Button onClick={() => copy("text")}>`}</code>
            <code className="block">{`  {copied ? "Copied!" : "Copy"}`}</code>
            <code className="block">{`</Button>`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* useMediaQuery */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>useMediaQuery</CardTitle>
              <CardDescription>Respond to media query changes</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${isDesktop ? "bg-success" : "bg-destructive"}`} />
              <Text as="p" className="text-sm">
                Desktop (&ge;1024px): <span className="font-medium">{isDesktop ? "Yes" : "No"}</span>
              </Text>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${prefersDark ? "bg-success" : "bg-destructive"}`} />
              <Text as="p" className="text-sm">
                Prefers dark mode: <span className="font-medium">{prefersDark ? "Yes" : "No"}</span>
              </Text>
            </div>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{`import { useMediaQuery, usePrefersDarkMode } from "@/hooks";`}</code>
            <code className="block mt-2">{`// Generic media query`}</code>
            <code className="block">{`const isDesktop = useMediaQuery("(min-width: 1024px)");`}</code>
            <code className="block mt-2">{`// Prebuilt helpers`}</code>
            <code className="block">{`const prefersDark = usePrefersDarkMode();`}</code>
            <code className="block">{`const reducedMotion = usePrefersReducedMotion();`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* useLocalStorage */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>useLocalStorage</CardTitle>
              <CardDescription>Persist state to localStorage</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => setCount((c) => c - 1)}>
              -
            </Button>
            <Text as="p" className="text-lg font-semibold w-12 text-center">
              {count}
            </Text>
            <Button variant="outline" onClick={() => setCount((c) => c + 1)}>
              +
            </Button>
            <Text as="p" className="text-sm text-muted-foreground ml-4">
              Persists across page reloads
            </Text>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{`import { useLocalStorage } from "@/hooks";`}</code>
            <code className="block mt-2">{`const [count, setCount] = useLocalStorage("key", 0);`}</code>
            <code className="block mt-2">{`// Works like useState but persists to localStorage`}</code>
            <code className="block">{`setCount(5);`}</code>
            <code className="block">{`setCount(prev => prev + 1);`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Available Hooks */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Available Hooks</CardTitle>
              <CardDescription>All hooks in the library</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-semibold">Hook</th>
                <th className="text-left py-2 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">useDebounce</td>
                <td className="py-2 text-muted-foreground">Debounce a value with configurable delay</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">useDebouncedCallback</td>
                <td className="py-2 text-muted-foreground">Debounce a callback function</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">useClickOutside</td>
                <td className="py-2 text-muted-foreground">Detect clicks outside an element</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">useLocalStorage</td>
                <td className="py-2 text-muted-foreground">Persist state to localStorage</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">useMediaQuery</td>
                <td className="py-2 text-muted-foreground">React to media query changes</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">useClipboard</td>
                <td className="py-2 text-muted-foreground">Copy text to clipboard</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">useLockBodyScroll</td>
                <td className="py-2 text-muted-foreground">Lock body scroll (for modals)</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">useEscapeKey</td>
                <td className="py-2 text-muted-foreground">Handle Escape key press</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">useFocusTrap</td>
                <td className="py-2 text-muted-foreground">Trap focus within an element</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">useIsMobile</td>
                <td className="py-2 text-muted-foreground">Check if viewport is mobile</td>
              </tr>
            </tbody>
          </table>
        </SectionCard>
      </Section>

      {/* Import */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Import</CardTitle>
              <CardDescription>How to import hooks</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{`// Import all from barrel export`}</code>
            <code className="block">{`import { useDebounce, useClipboard } from "@/hooks";`}</code>
            <code className="block mt-2">{`// Or import individually`}</code>
            <code className="block">{`import { useDebounce } from "@/hooks/use-debounce";`}</code>
          </div>
        </SectionCard>
      </Section>
    </>
  );
}
